import React, { useEffect } from "react";

/**
 * Auto-detects platform from URL and renders the right embed.
 * Supported: YouTube, Instagram, Facebook (video/post), Spotify, Twitter/X, generic iframe.
 */
function detect(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  // YouTube
  let m = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return { kind: "youtube", id: m[1] };
  // Spotify (track, episode, show, playlist, album)
  m = u.match(/open\.spotify\.com\/(track|episode|show|playlist|album|artist)\/([A-Za-z0-9]+)/);
  if (m) return { kind: "spotify", kindType: m[1], id: m[2] };
  // Instagram
  m = u.match(/instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/);
  if (m) return { kind: "instagram", id: m[1], rawUrl: u };
  // Facebook video (multiple URL shapes)
  if (/facebook\.com\/.*(?:videos|watch|reel|posts|story)/i.test(u)) {
    return { kind: "facebook", rawUrl: u };
  }
  // Twitter / X
  m = u.match(/(?:twitter\.com|x\.com)\/[^/]+\/status\/(\d+)/);
  if (m) return { kind: "twitter", id: m[1], rawUrl: u };
  // Direct embed URL (already an /embed path)
  if (/\/embed(\/|\?)/.test(u)) return { kind: "iframe", src: u };
  return null;
}

function YouTubeEmbed({ id }) {
  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-zinc-100 bg-black" style={{ paddingBottom: "56.25%" }}>
      <iframe
        src={`https://www.youtube.com/embed/${id}?rel=0`}
        title="YouTube video"
        className="absolute inset-0 w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
}

function SpotifyEmbed({ kindType, id }) {
  // Spotify embed responsive heights
  const height = kindType === "track" ? 152 : 352;
  return (
    <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-black">
      <iframe
        src={`https://open.spotify.com/embed/${kindType}/${id}?utm_source=generator`}
        title="Spotify"
        width="100%"
        height={height}
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
    </div>
  );
}

function InstagramEmbed({ rawUrl }) {
  // Load Instagram embed script once
  useEffect(() => {
    const id = "insta-embed-script";
    if (!document.getElementById(id)) {
      const s = document.createElement("script");
      s.id = id; s.async = true; s.src = "https://www.instagram.com/embed.js";
      document.body.appendChild(s);
    } else if (window.instgrm?.Embeds) {
      window.instgrm.Embeds.process();
    }
  }, [rawUrl]);
  return (
    <div className="flex justify-center my-2">
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={rawUrl}
        data-instgrm-version="14"
        style={{
          background: "#FFF", border: 0, borderRadius: "16px",
          boxShadow: "0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)",
          margin: "1px", maxWidth: "540px", minWidth: "326px", padding: 0, width: "99.375%",
        }}
      >
        <a href={rawUrl} target="_blank" rel="noopener noreferrer">View on Instagram</a>
      </blockquote>
    </div>
  );
}

function FacebookEmbed({ rawUrl }) {
  const src = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(rawUrl)}&show_text=false`;
  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-zinc-100 bg-black" style={{ paddingBottom: "56.25%" }}>
      <iframe
        src={src}
        title="Facebook video"
        className="absolute inset-0 w-full h-full"
        scrolling="no"
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
}

function TwitterEmbed({ rawUrl }) {
  useEffect(() => {
    const id = "twitter-embed-script";
    if (!document.getElementById(id)) {
      const s = document.createElement("script");
      s.id = id; s.async = true; s.src = "https://platform.twitter.com/widgets.js";
      document.body.appendChild(s);
    } else if (window.twttr?.widgets) {
      window.twttr.widgets.load();
    }
  }, [rawUrl]);
  return (
    <div className="flex justify-center">
      <blockquote className="twitter-tweet" data-theme="light">
        <a href={rawUrl} target="_blank" rel="noopener noreferrer">View Tweet</a>
      </blockquote>
    </div>
  );
}

function GenericIframe({ src }) {
  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-zinc-100" style={{ paddingBottom: "56.25%" }}>
      <iframe src={src} title="embed" className="absolute inset-0 w-full h-full" allowFullScreen loading="lazy" />
    </div>
  );
}

export default function MediaEmbed({ url, className = "" }) {
  const meta = detect(url);
  if (!meta) return null;
  return (
    <div className={`my-6 ${className}`} data-testid="media-embed">
      {meta.kind === "youtube" && <YouTubeEmbed id={meta.id} />}
      {meta.kind === "spotify" && <SpotifyEmbed kindType={meta.kindType} id={meta.id} />}
      {meta.kind === "instagram" && <InstagramEmbed rawUrl={meta.rawUrl} />}
      {meta.kind === "facebook" && <FacebookEmbed rawUrl={meta.rawUrl} />}
      {meta.kind === "twitter" && <TwitterEmbed rawUrl={meta.rawUrl} />}
      {meta.kind === "iframe" && <GenericIframe src={meta.src} />}
    </div>
  );
}

export { detect as detectMedia };
